import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  email: "",
  phone: "",
  property_address: "",
  package: "Listing + Drone",
  shoot_date: "",
  message: "",
};

const ContactForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent! We'll get back to you within a few hours.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:px-16 lg:py-32">
      <div className="grid grid-cols-1 gap-14 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="eyebrow-label mb-4">05 · Book a Shoot</p>
          <h2 className="font-display text-2xl font-bold tracking-tight text-zinc-50 sm:text-3xl lg:text-4xl">
            Let's make your next listing unforgettable
          </h2>
          <p className="mt-5 max-w-md text-base leading-relaxed text-zinc-400">
            Send the property address and your ideal shoot date — we'll confirm availability
            and a quote within a few hours.
          </p>

          <div className="mt-10 flex flex-col gap-5">
            <div className="flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
                <Mail size={17} className="text-[#E2C275]" />
              </span>
              <div>
                <div className="text-xs text-zinc-500">Email</div>
                <a href="mailto:coastalchoicesolutions@gmail.com" className="text-sm font-medium text-zinc-200 transition-colors hover:text-[#E2C275]">
                  coastalchoicesolutions@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
                <Phone size={17} className="text-[#E2C275]" />
              </span>
              <div>
                <div className="text-xs text-zinc-500">Phone</div>
                <a href="tel:+19717044781" className="text-sm font-medium text-zinc-200 transition-colors hover:text-[#E2C275]">
                  971-704-4781
                </a>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10">
                <MapPin size={17} className="text-[#E2C275]" />
              </span>
              <div>
                <div className="text-xs text-zinc-500">Service Area</div>
                <div className="text-sm font-medium text-zinc-200">Astoria, OR & the Oregon Coast</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          onSubmit={submit}
          data-testid="contact-enquiry-form"
          className="luxury-card p-8"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-xs font-medium text-zinc-400">Your name *</label>
              <input
                data-testid="contact-input-name"
                required
                value={form.name}
                onChange={set("name")}
                placeholder="Jordan Smith"
                className="input-lux"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium text-zinc-400">Email *</label>
              <input
                data-testid="contact-input-email"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder="jordan@realty.com"
                className="input-lux"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium text-zinc-400">Phone</label>
              <input
                data-testid="contact-input-phone"
                value={form.phone}
                onChange={set("phone")}
                placeholder="971-555-0100"
                className="input-lux"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium text-zinc-400">Property address</label>
              <input
                data-testid="contact-input-property-address"
                value={form.property_address}
                onChange={set("property_address")}
                placeholder="2689 Grand Ave, Astoria"
                className="input-lux"
              />
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium text-zinc-400">What do you need?</label>
              <select
                data-testid="contact-select-package"
                value={form.package}
                onChange={set("package")}
                className="input-lux"
              >
                <option>Interior + Exterior Photos</option>
                <option>Aerial Drone Photos</option>
                <option>Photos + Drone Bundle</option>
                <option>Twilight Add-On</option>
                <option>Not sure yet</option>
              </select>
            </div>
            <div>
              <label className="mb-2 block text-xs font-medium text-zinc-400">Preferred shoot date</label>
              <input
                data-testid="contact-input-date"
                type="date"
                value={form.shoot_date}
                onChange={set("shoot_date")}
                className="input-lux [color-scheme:dark]"
              />
            </div>
          </div>
          <div className="mt-5">
            <label className="mb-2 block text-xs font-medium text-zinc-400">Anything else we should know?</label>
            <textarea
              data-testid="contact-input-message"
              rows={4}
              value={form.message}
              onChange={set("message")}
              placeholder="Gate code, staging status, deadline for MLS launch…"
              className="input-lux resize-none"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-btn"
            className="btn-gold mt-7 w-full disabled:opacity-60"
          >
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {sending ? "Sending…" : "Send Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default ContactForm;
